import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@fox1ck-org/ui/toast";
import { Button, TooltipProvider } from "@fox1ck-org/ui";
import { api } from "@/api/client";
import { LogsToolbar } from "./LogsToolbar";
import { LogsFilterChips } from "./LogsFilterChips";
import { LogsVirtualList } from "./LogsVirtualList";
import { LogsVolumeChart } from "./LogsVolumeChart";
import { LogsEmptyState } from "./LogsEmptyState";
import { useLogsStream } from "./useLogsStream";
import { useLogsSearch } from "./useLogsSearch";
import { useLogsUrlState } from "./useLogsUrlState";
import { stripAnsi } from "./ansi";
import { levelRank, shortenPod } from "./levelStyles";
import type { LogsMode } from "./types";

export interface LogsViewerProps {
  /**
   * App context. When provided, live mode follows this app's pods and history
   * mode is scoped to the app's namespace.
   *
   * When omitted (the global `/logs` route), live mode is disabled and only
   * historical search across all namespaces is available.
   */
  appName?: string;
  /** Namespace to scope historical search to. Inferred from appName when used. */
  namespace?: string;
  /** Mode the viewer opens in (subject to URL override). */
  defaultMode?: LogsMode;
  /** When true, viewer occupies all available height (fixed-position page). */
  fullHeight?: boolean;
}

/**
 * Unified Logs Viewer — single component for both live (k8s SSE) and
 * historical (ClickHouse) modes. URL state is the source of truth so every
 * filter, mode change, pod selection, and search is shareable / refresh-safe.
 */
export function LogsViewer({
  appName,
  namespace,
  defaultMode = "history",
  fullHeight = true,
}: LogsViewerProps) {
  const { state, update, reset } = useLogsUrlState();

  // Apply default mode the first time the viewer mounts without an explicit
  // mode in the URL. Avoid clobbering an existing user-selected mode.
  useEffect(() => {
    if (!state.mode || state.mode === "live") {
      // URL state always has a mode (defaults to "live" in DEFAULT_URL_STATE)
    }
    if (defaultMode === "history" && !window.location.search.includes("mode=")) {
      update({ mode: "history" });
    } else if (defaultMode === "live" && !appName) {
      // Live mode requires an app — fall back to history.
      update({ mode: "history" });
    }
    // intentionally only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveMode: LogsMode = appName ? state.mode : "history";

  // ---- Pod options for the selector (live mode only) -----------------------
  const podsQuery = useQuery({
    queryKey: ["logs-viewer-pods", appName],
    queryFn: () => api.cluster.getPods(appName!),
    enabled: !!appName,
    refetchInterval: 30_000,
  });

  const podOptions = useMemo(() => {
    if (!podsQuery.data) return [] as Array<{ name: string; label: string }>;
    return podsQuery.data.pods.map((p) => ({
      name: p.name,
      label: shortenPod(p.name) + (p.ready ? "" : " · не готов"),
    }));
  }, [podsQuery.data]);

  const containerOptions = useMemo(() => {
    if (!podsQuery.data) return [] as string[];
    const all = new Set<string>();
    for (const p of podsQuery.data.pods) {
      for (const c of p.containers ?? []) all.add(c.name);
    }
    return Array.from(all);
  }, [podsQuery.data]);

  // ---- Live stream ---------------------------------------------------------
  const stream = useLogsStream({
    appName: effectiveMode === "live" ? appName : undefined,
    pod: state.pod || undefined,
    container: state.container || undefined,
    enabled: effectiveMode === "live",
  });

  // ---- Historical search ---------------------------------------------------
  const search = useLogsSearch({
    enabled: effectiveMode === "history",
    namespace: namespace,
    pod: state.pod || undefined,
    container: state.container || undefined,
    levels: state.levels,
    search: state.q,
    correlationId: state.cid,
    range: state.range,
  });

  // ---- Records (filtered, normalized) --------------------------------------
  const sourceRecords = effectiveMode === "live" ? stream.records : search.records;

  const filteredRecords = useMemo(() => {
    return sourceRecords.filter((r) => {
      if (state.levels.length > 0 && !state.levels.includes(r.level)) {
        // For unknown custom levels, allow if any selected level rank matches
        if (!state.levels.some((l) => levelRank(l) === levelRank(r.level))) return false;
      }
      if (state.q) {
        const needle = state.q.toLowerCase();
        const haystack = `${stripAnsi(r.msg)} ${r.pod}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [sourceRecords, state.levels, state.q]);

  // ---- Toolbar handlers ----------------------------------------------------
  const onModeChange = useCallback(
    (mode: LogsMode) => {
      update({ mode });
    },
    [update]
  );

  const onClearBuffer = useCallback(() => {
    if (effectiveMode === "live") {
      stream.clear();
    } else {
      reset();
    }
  }, [effectiveMode, stream, reset]);

  const onDownload = useCallback(() => {
    if (filteredRecords.length === 0) {
      toast.info("Нечего скачивать");
      return;
    }
    const text = filteredRecords
      .map((r) => `${r.ts}  ${r.level.padEnd(5)}  ${r.pod}  ${stripAnsi(r.msg)}`)
      .join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${appName ?? "logs"}-${new Date().toISOString().slice(0, 19)}.log`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Скачано строк: ${filteredRecords.length}`);
  }, [filteredRecords, appName]);

  const onCorrelationClick = useCallback(
    (cid: string) => {
      update({ mode: "history", cid, range: state.range === "15m" ? "1h" : state.range });
    },
    [update, state.range]
  );

  // ---- Available levels for the toolbar ------------------------------------
  const availableLevels = useMemo(() => {
    if (effectiveMode === "history" && search.filters?.levels?.length) {
      return search.filters.levels.map((l) => l.toUpperCase());
    }
    // Live mode: derive from current buffer.
    const found = new Set<string>();
    for (const r of stream.records.slice(-500)) found.add(r.level);
    if (found.size === 0) return ["ERROR", "WARN", "INFO", "DEBUG"];
    return Array.from(found);
  }, [effectiveMode, search.filters, stream.records]);

  // ---- Empty / loading state decision --------------------------------------
  const renderEmpty = (): React.ReactNode | null => {
    if (effectiveMode === "live") {
      if (!appName) {
        return (
          <LogsEmptyState
            variant="empty"
            message="Выберите приложение, чтобы смотреть журнал онлайн, или откройте историю."
          />
        );
      }
      if (stream.status === "missing-pod") {
        return (
          <LogsEmptyState
            variant="missing-pod"
            message={stream.error ?? "Сейчас у приложения нет запущенных подов."}
            onAction={() => stream.reconnect()}
            actionLabel="Повторить"
          />
        );
      }
      if (filteredRecords.length === 0) {
        if (stream.status === "connecting") {
          return <LogsEmptyState variant="loading" />;
        }
        if (stream.status === "reconnecting") {
          return (
            <LogsEmptyState
              variant="disconnected"
              reconnectAttempt={stream.reconnectAttempt}
              message={stream.error ?? undefined}
            />
          );
        }
        return <LogsEmptyState variant="stream-waiting" />;
      }
      return null;
    }

    if (search.isLoading) return <LogsEmptyState variant="loading" />;
    if (filteredRecords.length === 0) {
      return (
        <LogsEmptyState
          variant="empty"
          onAction={() => reset()}
          actionLabel="Снять фильтры"
        />
      );
    }
    return null;
  };

  const empty = renderEmpty();

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={
          fullHeight
            ? "flex flex-col h-[calc(100vh-180px)] min-h-[420px] gap-2"
            : "flex flex-col gap-2"
        }
      >
        <LogsToolbar
          mode={effectiveMode}
          onModeChange={onModeChange}
          pod={state.pod}
          onPodChange={(p) => update({ pod: p })}
          podOptions={podOptions}
          container={state.container}
          onContainerChange={(c) => update({ container: c })}
          containerOptions={containerOptions}
          levels={state.levels}
          onLevelsChange={(l) => update({ levels: l })}
          availableLevels={availableLevels}
          search={state.q}
          onSearchChange={(q) => update({ q })}
          range={state.range}
          onRangeChange={(r) => update({ range: r })}
          onClear={onClearBuffer}
          onDownload={onDownload}
          onRefresh={effectiveMode === "history" ? () => void search.refetch() : undefined}
          isFetching={effectiveMode === "history" && search.isFetching}
          streamStatus={effectiveMode === "live" ? stream.status : "idle"}
          reconnectAttempt={stream.reconnectAttempt}
        />

        <LogsFilterChips
          pod={state.pod}
          container={state.container}
          levels={state.levels}
          search={state.q}
          correlationId={state.cid}
          onClearPod={() => update({ pod: "" })}
          onClearContainer={() => update({ container: "" })}
          onRemoveLevel={(l) => update({ levels: state.levels.filter((x) => x !== l) })}
          onClearSearch={() => update({ q: "" })}
          onClearCorrelation={() => update({ cid: "" })}
          onClearAll={() => reset()}
        />

        {effectiveMode === "history" && search.stats && (
          <LogsVolumeChart stats={search.stats} rangeLabel={state.range} />
        )}

        {/* Result-count + history pagination */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
          <span>
            строк: {filteredRecords.length.toLocaleString("ru-RU")}
            {effectiveMode === "history" && search.total > filteredRecords.length && (
              <> · всего {search.total.toLocaleString("ru-RU")}</>
            )}
          </span>
          {effectiveMode === "history" && search.hasNextPage && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px]"
              onClick={() => void search.fetchNextPage()}
              disabled={search.isFetchingNextPage}
            >
              {search.isFetchingNextPage ? "Загружаем…" : "Загрузить ещё"}
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 rounded-lg border bg-card overflow-hidden">
          {empty ?? (
            <LogsVirtualList
              records={filteredRecords}
              searchTerm={state.q}
              showPod={!state.pod || effectiveMode === "history"}
              defaultFollowing={effectiveMode === "live"}
              followEnabled={effectiveMode === "live"}
              onSelectPod={(pod) => update({ pod })}
              onSelectContainer={(container) => update({ container })}
              onCorrelationClick={onCorrelationClick}
            />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
